import type { ReportColumn } from '../models/column.types'

export type CustomizeGroupKey = 'dimensions' | 'labels' | 'metrics'

export interface CustomizeGroup {
  key: CustomizeGroupKey
  columns: ReportColumn[]
}

export type CustomizeMetricGroupKey =
  | 'adPerformance'
  | 'ga4'
  | 'hardal'
  | 'mail'
  | 'firstParty'
  | 'attribution'
  | 'adapty'
  | 'adjust'
  | 'appsflyer'

export interface CustomizeMetricGroup {
  key: CustomizeMetricGroupKey
  columns: ReportColumn[]
}

const GROUP_ORDER: readonly CustomizeGroupKey[] = ['dimensions', 'labels', 'metrics']

/** v3 `OrphexTableSettings.vue:203-212`: labels are the `is_label` dimensions, not a third axis. */
export function customizeGroupKey(column: ReportColumn): CustomizeGroupKey {
  if (column.numeric) return 'metrics'
  return column.is_label ? 'labels' : 'dimensions'
}

/**
 * The customization panel's offer set, grouped the way v3 grouped it (ORP-19849).
 *
 * The breakdown column is withheld from both dimension groups — it is the column the rows are
 * grouped by, so hiding it is not a choice the user has (v3 `:205,:210`; v4 already excludes it in
 * `tablePresentation.ts:213,220`). Metrics keep it, exactly as v3 did. Empty groups are dropped so
 * a heading never renders over nothing (v3 gated each group on `…Options.length`).
 */
export function customizeGroups(
  columns: readonly ReportColumn[],
  breakdownField?: string | null,
): CustomizeGroup[] {
  return GROUP_ORDER.map((key) => ({
    key,
    columns: columns.filter(
      (column) =>
        customizeGroupKey(column) === key && (key === 'metrics' || column.field !== breakdownField),
    ),
  })).filter((group) => group.columns.length > 0)
}

/** Legacy table/chart settings grouped metrics by their stable field namespace. Keep that
 * information in a pure service so every customize surface presents the same hierarchy. */
export function customizeMetricGroupKey(column: ReportColumn): CustomizeMetricGroupKey {
  const field = column.field.toLowerCase()
  if (field.startsWith('mp_') || field.startsWith('key_events')) return 'ga4'
  if (field.startsWith('hardal_')) return 'hardal'
  if (field.includes('mail_metric_')) return 'mail'
  if (field.startsWith('fp_')) return 'firstParty'
  if (field.startsWith('attr_')) return 'attribution'
  if (field.startsWith('iap_')) return 'adapty'
  if (field.startsWith('mmp_')) {
    return column.headerImage?.toLowerCase().includes('appsflyer') ? 'appsflyer' : 'adjust'
  }
  return 'adPerformance'
}

/** Preserve first-seen group order, matching the legacy panel and the offered column order. */
export function customizeMetricGroups(columns: readonly ReportColumn[]): CustomizeMetricGroup[] {
  const groups = new Map<CustomizeMetricGroupKey, ReportColumn[]>()
  for (const column of columns) {
    const key = customizeMetricGroupKey(column)
    const current = groups.get(key) ?? []
    current.push(column)
    groups.set(key, current)
  }
  return [...groups].map(([key, groupedColumns]) => ({ key, columns: groupedColumns }))
}
